import React, { useState } from 'react';
import { useServices } from '../../core/services/useServices';
import { LoadingOutlined, PushpinOutlined } from '@ant-design/icons';
import Endpoints, {
  ALL_EXCLUDED, ALL_EXCLUDED_RR,
  PODIUM_TITLES, RETURN, SOURCE, WATCHLIST, WATCHLIST_AO3, WATCHLIST_RR
} from '../../core/constants/endpoints';
import { Input, Checkbox } from 'antd';
import {
  TAB_NAME
} from '../../core/constants';
import { useQueryClient } from 'react-query';
import { Spinner } from '../../components/Spinner';
import styled from 'styled-components';
import { SpinnerWrapper } from '../../components/styles';
import Modal from '../../components/Modal';
import { useActions } from '../../context/Actions.context';
import { useHistory } from 'react-router-dom';
import showActionNotification from '../../section/Common/utils/showActionNotification';

const { TextArea } = Input;

const WarningText = styled.p`
  font-size: 16px;
  margin-right: 12px;
`;

interface IProps {
  book_ids: [];
  onClose: Function;
  onReset: Function;
  open: boolean;
  id: string;
  name: string;
}

const SendAll: React.FC<IProps> = ({ open, id, book_ids, name, onClose, onReset }) => {
  const { book } = useServices();
  const { updateTitle, returnTitle } = book;
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');
  const [pinOnSend, setPinOnSend] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const data: { [key: string]: any } = {};
  const { source: sourcePath, setActionLoading } = useActions();
  const history = useHistory();
  const sourceTab = window.location.pathname.split('/')[2];
  const isActionPodiumTitles = name === PODIUM_TITLES ? id !== ALL_EXCLUDED && id !== ALL_EXCLUDED_RR : false;
  const { book: bookService } = useServices();

  if (sourcePath['source'] === SOURCE.bookstat) {
    data.amazonasin = book_ids;
  } else if (sourcePath['source'] === SOURCE['royal-road']) {
    data.fiction_id = book_ids;
  } else if (sourcePath['source'] === SOURCE.ao3) {
    data.work_id = book_ids;
  }

  const handleClose = () => {
    setNote('');
    setPinOnSend(false);
    onClose();
  };

  const onSend = async () => {
    setActionLoading(true);
    setLoading(true);

    try {
      const payload = (note?.trim() || pinOnSend)
        ? {
          ...(note?.trim() && { notes: note.trim() }),
          ...(pinOnSend && { is_pinned_watchlist: 1 })
        }
        : undefined;

      const response = await updateTitle(
        {
          source: sourcePath['source'],
          endpoint: Endpoints[name],
          sourceTab,
          action: name
        },
        data,
        payload
      );

      if (isActionPodiumTitles) {
        await updateTitle(
          {
            source: sourcePath['source'],
            endpoint: Endpoints[ALL_EXCLUDED],
            sourceTab,
            action: ALL_EXCLUDED
          },
          data,
          payload
        );
      }

      setLoading(false);
      handleClose();
      onReset();

      if (response?.status === 200) {
        const capturedSource = sourcePath['source'];
        const capturedData = { ...data };
        const route = Endpoints[name];

        showActionNotification({
          type: 'success',
          message: `Successfully sent title(s) to the ${TAB_NAME[name]} tab.`,
          ...(route
            ? {
              destination: {
                name: TAB_NAME[name] || name,
                onGoTo: () => history.push(`${capturedSource}${route}`)
              }
            }
            : {}),
          onUndo: async () => {
            await bookService.updateTitle(
              {
                source: capturedSource,
                sourceTab: route.replace('/', ''),
                endpoint: Endpoints[id],
                action: id
              },
              capturedData
            );
          },
          queryClient,
          id
        });

        if (isActionPodiumTitles) {
          showActionNotification({
            type: 'success',
            message: `Successfully sent title(s) to the ${TAB_NAME[ALL_EXCLUDED]} tab.`
          });
        }

        queryClient.refetchQueries(`get-row-count${id}`);
        queryClient.refetchQueries(`get-${id}`);
        return;
      }

      showActionNotification({ type: 'error', message: 'The title(s) have not been sent.' });
    } catch (error) {
      setLoading(false);
      onClose();
      onReset();

      showActionNotification({ type: 'error', message: 'Failed to send title(s).' });

      console.error('Failed to update title(s)', {
        action: name,
        sourceTab,
        error
      });
    } finally {
      setActionLoading(false);
    }
  };

  const onReturn = async () => {
    setActionLoading(true);
    setLoading(true);

    try {
      const payload = note?.trim() ? { notes: note.trim() } : undefined;

      const response = await returnTitle(
        id,
        sourcePath['source'],
        {
          params: data,
          body: payload
        }
      );

      setLoading(false);
      handleClose();
      onReset();

      if (response?.status === 200) {
        const capturedSource = sourcePath['source'];
        const route = capturedSource === '/ao3' ? 'popular' : 'all-new-titles';
        const capturedData = { ...data };
        showActionNotification({ type: 'success',
          message: 'Successfully returned title(s).',
          destination: {
            name: capturedSource === '/ao3' ? 'Popular' : 'All New Titles',
            onGoTo: () => history.push(`${capturedSource}/${route}`)
          },
          onUndo: async () => {
            await bookService.updateTitle(
              {
                source: capturedSource,
                sourceTab: route,
                endpoint: Endpoints[id],
                action: id
              },
              capturedData
            );
          },
          queryClient,
          id });
        await queryClient.refetchQueries(`get-row-count${id}`);
        await queryClient.refetchQueries(`get-${id}`);
        return;
      }

      showActionNotification({ type: 'error', message: 'The title(s) have not been returned.' });
    } catch (error) {
      setLoading(false);
      handleClose();
      onReset();

      showActionNotification({ type: 'error', message: 'Failed to return title(s).' });

      console.error('Failed to update title(s)', {
        action: name,
        sourceTab,
        error
      });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Modal
      okText='Yes'
      cancelText='No'
      visible={open}
      onOk={name === RETURN ? onReturn : onSend}
      onCancel={handleClose}
      confirmLoading={loading}
    >
      {name === RETURN ?
        <WarningText>
          You are about to return {book_ids?.length} title(s).
          Do you wish to continue?
        </WarningText> :
        <WarningText>
          You are about to send {book_ids?.length} title(s) to {TAB_NAME[name]}.
          Do you wish to continue?
        </WarningText>
      }

      <br />

      <TextArea
        placeholder='Leave a note stating the reason (optional)'
        maxLength={400}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={4}
      />

      <div style={{ textAlign: 'right', fontSize: 12, marginTop: 4 }}>
        {note.length} / 400
      </div>

      {(name === WATCHLIST || name === WATCHLIST_RR || name === WATCHLIST_AO3) && (
        <div style={{ marginTop: 16 }}>
          <Checkbox
            checked={pinOnSend}
            onChange={(e) => setPinOnSend(e.target.checked)}
          >
            <PushpinOutlined style={{ marginRight: 6 }} />
            Pin title(s) to the top of Watchlist
          </Checkbox>
        </div>
      )}

      {loading && (
        <SpinnerWrapper>
          <Spinner
            size='large'
            spinning={loading}
            indicator={
              <LoadingOutlined
                style={{
                  fontSize: 34
                }}
                spin
              />
            }
          />
        </SpinnerWrapper>
      )}
    </Modal>
  );
};

export default SendAll;